import { useState } from 'react';
import { Avatar } from '../../components/Avatar.tsx';
import { Icon } from '../../components/Icon.tsx';
import { Alert } from '../../components/Overlay.tsx';
import type { Group, Person } from '../../domain.ts';

/**
 * F3-2 구성원 리스트 · F3-2-1 구성원 편집.
 *
 * **한 창의 두 상태입니다.** 편집을 누르면 같은 창 안에서 바뀌고, 편집 자리가
 * 완료로 바뀝니다 — 화면을 갈아 끼우면 어디서 왔는지 잃어버립니다.
 *
 * 크기가 고정된 팝업입니다. 인원이 늘어도 창은 그대로이고 이름 목록만 안에서
 * 스크롤됩니다 — 아래쪽이 잘려 보이는 것이 «더 있다»는 표시입니다.
 *
 * **빼기는 두 번에 나눕니다.** 빨간 빼기 버튼을 누르면 그 줄 오른쪽에
 * 「내보내기」가 나타나고, 그것을 눌러야 빠집니다 — 스친 손가락 하나로
 * 사람이 빠지지 않게. 그룹장 줄에는 빼기 자리를 비웁니다: 모임을 나가는 일은
 * F3-1-3 에서 하고, 거기서는 넘길 사람을 함께 고릅니다.
 */

interface Props {
  group: Group;
  onClose: () => void;
  /** 남길 구성원을 정해 보냅니다. 그룹장은 서버가 지키므로 보내지 않아도 남습니다 */
  onSave: (members: string[]) => void;
  /** 구성원 추가는 모임 수정 화면이 합니다 — 친구 목록이 거기 있습니다 */
  onAdd: () => void;
}

export function MemberPopup({ group, onClose, onSave, onAdd }: Props) {
  const [editing, setEditing] = useState(false);
  /** 빼기를 눌러 「내보내기」가 떠 있는 줄 */
  const [arming, setArming] = useState<string | null>(null);
  const [members, setMembers] = useState<Person[]>(group.members);

  const kick = (userId: string) => {
    setMembers((was) => was.filter((m) => m.userId !== userId));
    setArming(null);
  };

  return (
    <Alert
      title=""
      onClose={onClose}
      actions={
        editing ? (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              onSave(members.map((m) => m.userId));
              setEditing(false);
            }}
          >
            완료
          </button>
        ) : (
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            취소
          </button>
        )
      }
    >
      <div className="mpop">
        <div className="mpop__head">
          <span className="mpop__title">구성원 ({members.length})</span>
          {/* 편집은 그룹장에게만 보입니다 */}
          {group.canManage && (
            <button
              type="button"
              className="mpop__action"
              onClick={() => {
                setArming(null);
                if (editing) {
                  onSave(members.map((m) => m.userId));
                }
                setEditing((v) => !v);
              }}
            >
              {editing ? '완료' : '편집'}
            </button>
          )}
        </div>

        <div className="mpop__list">
          {/* 구성원 추가는 친구 목록이 있는 모임 수정 화면으로 보냅니다 */}
          {editing && (
            <button type="button" className="pickrow" onClick={onAdd}>
              <span className="phslot__plus">
                <Icon name="plus" size={15} />
              </span>
              <span className="pickrow__name">구성원 추가</span>
              <Icon name="forward" size={16} />
            </button>
          )}

          {members.map((m) => {
            const owner = m.userId === group.ownerId;
            return (
              <div className="mrow2" key={m.userId}>
                {editing &&
                  (owner ? (
                    /* 그룹장 줄은 빼기 자리를 비웁니다 — 나가기는 F3-1-3 */
                    <span className="mrow2__hold" />
                  ) : (
                    <button
                      type="button"
                      className="mrow2__minus"
                      aria-label={`${m.name ?? '구성원'} 빼기`}
                      onClick={() => setArming(arming === m.userId ? null : m.userId)}
                    >
                      <Icon name="close" size={13} />
                    </button>
                  ))}

                <Avatar name={m.name ?? '?'} seed={m.userId} />
                <span className="mrow2__name">
                  {m.name ?? '이름 없음'}
                  {owner && <span className="rec__tag">그룹장</span>}
                </span>

                {/* 한 번 더 눌러야 실제로 빠집니다 */}
                {editing && arming === m.userId && (
                  <button type="button" className="mrow2__kick" onClick={() => kick(m.userId)}>
                    내보내기
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {editing && (
          <p className="hint">
            빼기(<b>✕</b>)를 누르고 <b>내보내기</b>까지 눌러야 빠집니다. 완료를 누르면 반영됩니다.
          </p>
        )}
      </div>
    </Alert>
  );
}
